import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '~/components/HeaderNotification';
import { Shield, Save, X, Eye, EyeOff } from 'lucide-react';
import JsonRoute from '~/assets/routes.json';

const apiUrl = process.env.REACT_APP_API_URL;

// Lấy tất cả các route hợp lệ từ JsonRoute (chỉ lấy Area: "AdminArea" và bỏ route động)
const availableRoutes = JsonRoute.filter(r => r.Path && r.Path.trim() !== '' && !r.Path.includes(':') && r.Area === "AdminArea").map(r => r.Path);

const SecuritySettingsModal = ({ show, onClose }) => {
    const [selectedRoutes, setSelectedRoutes] = useState([]);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show) {
            fetchSettings();
            setNewPassword('');
            setConfirmPassword('');
            setError(null);
        }
    }, [show]);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${apiUrl}/api/admin/security/settings`, { withCredentials: true });
            if (res.data?.Succeeded) {
                const routesStr = res.data.Data.LockedRoutes || '';
                const routesArr = routesStr.split(',').map(r => r.trim()).filter(r => r);
                setSelectedRoutes(routesArr);
            }
        } catch (error) {
            console.error("Error fetching security settings", error);
            toast.error("Failed to load security settings");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        setError(null);

        if (newPassword && newPassword !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        setIsSaving(true);
        try {
            const res = await axios.post(`${apiUrl}/api/admin/security/update-settings`, {
                LockedRoutes: selectedRoutes.join(','),
                NewPassword: newPassword
            }, { withCredentials: true });

            if (res.data?.Succeeded) {
                toast.success("Security settings updated successfully");
                onClose();
            } else {
                setError(res.data?.Message || "Failed to update settings");
            }
        } catch (err) {
            console.error("Error saving security settings", err);
            setError("An error occurred while saving");
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggleRoute = (routePath) => {
        if (selectedRoutes.includes(routePath)) {
            setSelectedRoutes(selectedRoutes.filter(r => r !== routePath));
        } else {
            setSelectedRoutes([...selectedRoutes, routePath]);
        }
    };

    if (!show) return null;

    return (
        <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
            <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1051, backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content border-0 shadow rounded-4">
                        <div className="modal-header border-bottom px-4 py-3 bg-light rounded-top-4">
                            <h5 className="modal-title fw-bold text-body-emphasis d-flex align-items-center">
                                <Shield size={20} className="me-2 text-primary" /> Security Settings
                            </h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body p-4">
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="row g-4">
                                    {error && (
                                        <div className="col-12 mb-0 pb-0">
                                            <div className="alert alert-danger py-2 px-3 mb-0 d-flex align-items-center fs-9 fw-bold">
                                                <Shield size={16} className="me-2 flex-shrink-0" />
                                                {error}
                                            </div>
                                        </div>
                                    )}
                                    <div className="col-12">
                                        <label className="form-label fw-bold text-body-emphasis fs-8">Super Admin Password</label>
                                        <p className="text-body-tertiary fw-bold fs-9 mb-2">
                                            Set or update the super admin password required to access locked screens. Leave blank to keep current password.
                                        </p>
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <div className="position-relative">
                                                    <input 
                                                        type={showPassword ? "text" : "password"} 
                                                        className="form-control pe-5" 
                                                        placeholder="New Password"
                                                        value={newPassword}
                                                        onChange={(e) => setNewPassword(e.target.value)}
                                                    />
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-body-tertiary p-0 px-3 text-decoration-none"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                    >
                                                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="position-relative">
                                                    <input 
                                                        type={showConfirmPassword ? "text" : "password"} 
                                                        className="form-control pe-5" 
                                                        placeholder="Confirm Password"
                                                        value={confirmPassword}
                                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                                    />
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-body-tertiary p-0 px-3 text-decoration-none"
                                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    >
                                                        {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <label className="form-label fw-bold text-body-emphasis fs-8">Locked Routes</label>
                                        <p className="text-body-tertiary fw-bold fs-9 mb-2">
                                            Select the exact URL paths that require the Super Admin Password to access.
                                        </p>
                                        <div className="border rounded-2 p-2 bg-white px-3" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                            {availableRoutes.map((route, idx) => {
                                                const isSelected = selectedRoutes.includes(route);
                                                return (
                                                    <div 
                                                        key={idx} 
                                                        className={`d-flex align-items-center mb-1 py-2 px-3 rounded-2 cursor-pointer ${isSelected ? 'bg-primary bg-opacity-10' : ''}`}
                                                        style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                                                        onClick={() => handleToggleRoute(route)}
                                                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                                                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                    >
                                                        <input 
                                                            className="form-check-input m-0 me-3 cursor-pointer flex-shrink-0" 
                                                            type="checkbox" 
                                                            checked={isSelected}
                                                            onChange={() => {}} // Handle on parent div
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                        <label className="text-body-secondary fw-bold fs-9 cursor-pointer mb-0 text-truncate" style={{ cursor: 'pointer' }} title={route}>
                                                            {route}
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer border-top bg-light p-3 justify-content-end rounded-bottom-4">
                            <button type="button" className="btn btn-sm btn-light fw-bold px-3 text-body-secondary border" onClick={onClose} disabled={isSaving}>
                                Cancel
                            </button>
                            <button type="button" className="btn btn-sm btn-primary fw-bold px-4 d-flex align-items-center" onClick={handleSave} disabled={isSaving || isLoading}>
                                {isSaving ? <span className="spinner-border spinner-border-sm me-2" /> : <Save size={16} className="me-2" />}
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SecuritySettingsModal;
